import React, { useState } from 'react';
import axios from 'axios';
import {
    Dialog,
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Divider,
    Grid,
    Tooltip,
    CircularProgress
} from '@mui/material';
import {
    Close,
    History,
    Inventory2,
    QrCode2,
    ContentCopy,
    CheckCircle,
    Warning,
    Replay,
    FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { Button, Snackbar, Alert } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DocumentDetails } from './AdminDocuments';

// Цветовая схема
const colors = {
    primary: '#6366f1',
    secondary: '#8b5cf6',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
    dark: '#1e293b',
    light: '#f8fafc',
    border: '#e2e8f0',
    white: '#ffffff'
};

const getStatusColor = (status: number): string => {
    switch (status) {
        case 1: return colors.info;     // Новый
        case 2: return colors.primary;  // Передан
        case 3: return colors.success;  // Принят
        case 4: return colors.success;  // Принят частично
        case 5: return colors.error;    // Отклонен
        case 6: return '#64748b';       // В пути
        case 7: return colors.info;     // В работе
        case 9: return colors.warning;  // Черновик
        case 10: return colors.success; // Принят с расхождениями
        case 11: return colors.error;   // Ошибка остатка
        case 12: return colors.warning; // Сканирование коробов
        case 13: return colors.warning; // Подтвердить отправку
        case 14: return colors.error;   // Ошибка 1С
        case 15: return '#22c55e';      // Ожидает подтверждения
        case 16: return '#64748b';      // Завершен
        default: return '#94a3b8';
    }
};

const CopyButton: React.FC<{ text: string | number }> = ({ text }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(String(text));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    if (!text) return null;

    return (
        <Tooltip title={copied ? "Скопировано!" : "Копировать"} arrow>
            <IconButton onClick={handleCopy} size="small" sx={{ p: 0.5, ml: 0.5, opacity: 0.5, '&:hover': { opacity: 1 } }}>
                {copied ? <CheckCircle sx={{ fontSize: 14, color: colors.success }} /> : <ContentCopy sx={{ fontSize: 14 }} />}
            </IconButton>
        </Tooltip>
    );
};

// Компонент поля
const Field: React.FC<{ label: string; value: React.ReactNode; icon?: React.ReactNode; fullWidth?: boolean; copyable?: boolean }> = ({ label, value, icon, fullWidth, copyable }) => {
    if (!value || value === '—' || value === '') return null;
    return (
        <Box sx={{ mb: 2, minWidth: fullWidth ? '100%' : 'auto' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, fontWeight: 500 }}>
                {icon && <Box component="span" sx={{ opacity: 0.7, transform: 'scale(0.8)', display: 'flex' }}>{icon}</Box>}
                {label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary', wordBreak: 'break-word' }}>
                    {value}
                </Typography>
                {copyable && (typeof value === 'string' || typeof value === 'number') && <CopyButton text={value} />}
            </Box>
        </Box>
    );
};

const SectionTitle: React.FC<{ title: string; icon?: React.ReactNode; action?: React.ReactNode }> = ({ title, icon, action }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, pb: 1.5, borderBottom: `1px solid ${colors.border}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ color: colors.primary, display: 'flex', p: 0.5, bgcolor: alpha(colors.primary, 0.1), borderRadius: 1 }}>{icon}</Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.dark }}>
                {title}
            </Typography>
        </Box>
        {action}
    </Box>
);

const API_BASE = 'https://tbl.zerdegroup.kz/api/v1';

interface AdminDocumentViewDialogProps {
    open: boolean;
    document: DocumentDetails | null;
    loading?: boolean;
    onClose: () => void;
    onRefreshDocument?: (docId: number) => void;
}

const AdminDocumentViewDialog: React.FC<AdminDocumentViewDialogProps> = ({
    open,
    document,
    loading = false,
    onClose,
    onRefreshDocument
}) => {
    const [retryLoading, setRetryLoading] = useState(false);
    const [retrySnack, setRetrySnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false, message: '', severity: 'success'
    });

    const handleRetry = async () => {
        if (!document) return;
        const username = localStorage.getItem('username') || 'Менеджер';
        setRetryLoading(true);
        try {
            const res = await axios.post(`${API_BASE}/admin/documents/${document.id}/retry`, { username });
            if (res.data.success) {
                setRetrySnack({ open: true, message: res.data.message, severity: 'success' });
                if (onRefreshDocument) onRefreshDocument(document.id);
            } else {
                setRetrySnack({ open: true, message: res.data.message || 'Ошибка при отправке', severity: 'error' });
            }
        } catch (err: any) {
            const msg = err?.response?.data?.message || 'Ошибка при повторной отправке';
            setRetrySnack({ open: true, message: msg, severity: 'error' });
        } finally {
            setRetryLoading(false);
        }
    };

    // Выгрузка кодов маркировки документа
    const [exportMarksStatus, setExportMarksStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [exportMarksError, setExportMarksError] = useState('');

    const totalMarksCount = React.useMemo(() => {
        if (!document?.items) return 0;
        return document.items.reduce((acc: number, it: any) => acc + (it.markCodes?.length || 0), 0);
    }, [document]);

    const handleExportMarkingCodes = async () => {
        if (!document) return;
        setExportMarksStatus('loading');
        setExportMarksError('');

        try {
            const response = await axios.get(
                `${API_BASE}/documents/${document.id}/marking-codes/export`,
                {
                    params: { version: '1.0.9' },
                    responseType: 'blob'
                }
            );

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = window.document.createElement('a');
            link.href = downloadUrl;
            link.download = `marking_codes_doc_${document.id}.xlsx`;
            window.document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);

            setExportMarksStatus('success');
            setTimeout(() => setExportMarksStatus('idle'), 3000);
        } catch (err: any) {
            console.error('Ошибка при выгрузке КМ документа:', err);
            let errorMsg = 'Ошибка при выгрузке КМ';
            if (err.response?.data instanceof Blob) {
                try {
                    const errorJson = JSON.parse(await err.response.data.text());
                    errorMsg = errorJson.message || errorMsg;
                } catch (_) { }
            } else if (err.response?.data?.message) {
                errorMsg = err.response.data.message;
            }
            setExportMarksStatus('error');
            setExportMarksError(errorMsg);
            setTimeout(() => {
                setExportMarksStatus('idle');
                setExportMarksError('');
            }, 5000);
        }
    };

    if (!open) return null;

    // Calculate if we need to show the status column
    const hasStatusColumn = document ? document.items.some(item => {
        const itemStatus = item.processStatus;
        return itemStatus && itemStatus !== 'Проведен';
    }) : false;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    height: '90vh',
                    bgcolor: '#f8fafc',
                    borderRadius: 3,
                    overflow: 'hidden'
                }
            }}
        >
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                </Box>
            ) : document ? (
                <>
                    {/* Header */}
                    <Paper elevation={0} sx={{
                        px: 3, py: 2,
                        borderBottom: `1px solid ${colors.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: 'white',
                        zIndex: 1,
                        flexShrink: 0
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5 }}>ДОКУМЕНТ</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 800, color: colors.dark, lineHeight: 1 }}>
                                        #{document.id}
                                    </Typography>
                                    <CopyButton text={document.id} />
                                </Box>
                            </Box>

                            <Divider orientation="vertical" flexItem sx={{ height: 32, alignSelf: 'center', mx: 1 }} />

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: alpha(getStatusColor(document.status), 0.1), px: 2, py: 0.5, borderRadius: 2 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getStatusColor(document.status), boxShadow: `0 0 0 2px ${alpha(getStatusColor(document.status), 0.3)}` }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.dark }}>
                                    {document.statusName}
                                </Typography>
                            </Box>

                            <Chip
                                label={document.type === 5 ? 'ПЕРЕМЕЩЕНИЕ' : 'ВОЗВРАТ'}
                                size="small"
                                sx={{
                                    borderRadius: 1,
                                    fontWeight: 600,
                                    bgcolor: colors.light,
                                    border: `1px solid ${colors.border}`,
                                    color: 'text.secondary',
                                    fontSize: '0.7rem'
                                }}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Button
                                onClick={handleExportMarkingCodes}
                                variant="outlined"
                                size="small"
                                disabled={exportMarksStatus === 'loading'}
                                startIcon={
                                    exportMarksStatus === 'loading' ? <CircularProgress size={16} color="inherit" /> :
                                        exportMarksStatus === 'success' ? <CheckCircle /> :
                                            exportMarksError ? <Close /> :
                                                <FileDownloadIcon />
                                }
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.82rem',
                                    borderRadius: 1.5,
                                    minWidth: exportMarksStatus === 'error' && exportMarksError ? 'auto' : undefined,
                                    borderColor: exportMarksStatus === 'success' ? colors.success
                                        : exportMarksStatus === 'error' ? colors.error
                                            : colors.border,
                                    color: exportMarksStatus === 'success' ? colors.success
                                        : exportMarksStatus === 'error' ? colors.error
                                            : colors.dark,
                                    bgcolor: exportMarksStatus === 'success' ? alpha(colors.success, 0.08)
                                        : exportMarksStatus === 'error' ? alpha(colors.error, 0.08)
                                            : 'transparent',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                {exportMarksStatus === 'success' ? 'Выгружено!'
                                    : exportMarksError ? (exportMarksError || 'Ошибка')
                                        : exportMarksStatus === 'loading' ? 'Выгрузка...'
                                            : totalMarksCount > 0 ? `Выгрузка КМ'ов (${totalMarksCount})`
                                                : "Выгрузка КМ'ов"}
                            </Button>

                            <IconButton onClick={onClose} size="small" sx={{ border: `1px solid ${colors.border}`, borderRadius: 2, transition: '0.2s', '&:hover': { bgcolor: colors.error, color: 'white', borderColor: colors.error } }}>
                                <Close fontSize="small" />
                            </IconButton>
                        </Box>
                    </Paper>

                    <Snackbar
                        open={retrySnack.open}
                        autoHideDuration={4000}
                        onClose={() => setRetrySnack(p => ({ ...p, open: false }))}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <Alert
                            onClose={() => setRetrySnack(p => ({ ...p, open: false }))}
                            severity={retrySnack.severity}
                            variant="filled"
                            sx={{ width: '100%', borderRadius: 2, fontWeight: 600 }}
                        >
                            {retrySnack.message}
                        </Alert>
                    </Snackbar>

                    <Box sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden' }}>
                        {/* Sidebar / Left Panel - Info, History, Boxes */}
                        <Box sx={{ width: 380, borderRight: `1px solid ${colors.border}`, bgcolor: 'white', overflow: 'auto', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>

                            <Box sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                                    {/* Combined Sender/Receiver */}
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Отправитель</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark, lineHeight: 1.2 }}>{document.fromPharmacy}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Получатель</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark, lineHeight: 1.2 }}>{document.toPharmacy}</Typography>
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{ borderStyle: 'dashed' }} />
                                    <Grid container spacing={2}>
                                        <Grid item><Field label="Статус 1С" value={document.processStatus} /></Grid>
                                    </Grid>
                                    <Divider sx={{ borderStyle: 'dashed' }} />

                                    <Box>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Создан / Обновлен</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{document.createdAt}</Typography>
                                        <Typography variant="caption" color="text.secondary">{document.updateDate}</Typography>
                                    </Box>

                                    <Field label="Входящий" value={document.incomingNumber ? `${document.incomingNumber} от ${document.incomingDate}` : null} copyable />
                                </Box>

                                {document.comment && (
                                    <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: alpha(colors.warning, 0.05), borderColor: alpha(colors.warning, 0.2) }}>
                                        <Typography variant="caption" gutterBottom sx={{ color: colors.warning, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Warning fontSize="inherit" /> КОММЕНТАРИЙ
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: colors.dark }}>{document.comment}</Typography>
                                    </Paper>
                                )}
                            </Box>

                            <Divider />

                            {/* Boxes Section */}
                            <Box sx={{ p: 3 }}>
                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: colors.dark, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Inventory2 fontSize="small" color="disabled" />
                                    Коробы ({document.boxes.length})
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {document.boxes.length > 0 ? document.boxes.map((box) => (
                                        <Tooltip key={box.id} title={`ШК: ${box.barcode}\nДата: ${box.recordDate}`} arrow>
                                            <Chip
                                                label={`№${box.boxNum} • ${box.barcode.slice(-4)}`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ borderRadius: 1, bgcolor: colors.light, borderColor: colors.border, fontSize: '0.75rem', height: 24 }}
                                            />
                                        </Tooltip>
                                    )) : (
                                        <Typography variant="caption" color="text.secondary">Нет коробов</Typography>
                                    )}
                                </Box>
                            </Box>

                            <Divider />

                            {/* History Section */}
                            <Box sx={{ p: 3, flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.dark, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <History fontSize="small" color="disabled" />
                                        История статусов
                                    </Typography>
                                    {document.canRetry && (
                                        <Button
                                            id="btn-retry-document"
                                            variant="contained"
                                            size="small"
                                            startIcon={retryLoading ? <CircularProgress size={14} color="inherit" /> : <Replay fontSize="small" />}
                                            disabled={retryLoading}
                                            onClick={handleRetry}
                                            sx={{
                                                bgcolor: colors.warning,
                                                color: 'white',
                                                fontWeight: 700,
                                                fontSize: '0.7rem',
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 1.5,
                                                boxShadow: 'none',
                                                '&:hover': { bgcolor: '#d97706', boxShadow: 'none' },
                                                '&:disabled': { bgcolor: '#fbbf24', color: 'white' }
                                            }}
                                        >
                                            {retryLoading ? 'Отправка...' : 'Обработать'}
                                        </Button>
                                    )}
                                </Box>
                                <Box sx={{ position: 'relative', pl: 1 }}>
                                    <Box sx={{ position: 'absolute', top: 4, bottom: 4, left: 12, width: 2, bgcolor: colors.light }} />
                                    {document.statusHistory.map((h, idx) => (
                                        <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 2.5, position: 'relative' }}>
                                            <Box sx={{
                                                width: 10, height: 10, borderRadius: '50%', mt: 0.5, flexShrink: 0,
                                                bgcolor: idx === 0 ? getStatusColor(h.statusZid) : colors.border,
                                                boxShadow: idx === 0 ? `0 0 0 2px ${alpha(getStatusColor(h.statusZid), 0.3)}` : 'none',
                                                zIndex: 1
                                            }} />
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark, lineHeight: 1.2 }}>
                                                    {h.statusName}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                                                    {h.recordDate} • {h.username}
                                                </Typography>
                                                {h.description && (
                                                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic', bgcolor: colors.light, p: 0.5, borderRadius: 1 }}>
                                                        {h.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>

                        {/* Main Content - Items Table */}
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', bgcolor: '#f8fafc', overflow: 'hidden' }}>
                            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'white', borderBottom: `1px solid ${colors.border}` }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.dark }}>
                                    Товары ({document.items.length})
                                </Typography>
                                {document.totalCost > 0 && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: alpha(colors.success, 0.08), border: `1px solid ${alpha(colors.success, 0.25)}`, borderRadius: 1.5, px: 1.5, py: 0.5 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>ИТОГО:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 800, color: colors.success }}>
                                            {document.totalCost.toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₸
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            <Box sx={{ p: 3, flexGrow: 1, overflow: 'auto' }}>
                                <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${colors.border}`, overflow: 'hidden', bgcolor: 'white' }}>
                                    <TableContainer sx={{ maxHeight: 'calc(85vh - 140px)' }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ bgcolor: colors.light, fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' }}>№</TableCell>
                                                    <TableCell sx={{ bgcolor: colors.light, fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary' }}>НАИМЕНОВАНИЕ</TableCell>
                                                    <TableCell sx={{ bgcolor: colors.light, fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', width: 100 }}>КОДЫ</TableCell>
                                                    <TableCell sx={{ bgcolor: colors.light, fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', width: 100 }}>КОЛ-ВО</TableCell>
                                                    <TableCell sx={{ bgcolor: colors.light, fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', width: 80 }}>1С</TableCell>
                                                    <TableCell sx={{ bgcolor: colors.light, fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', width: 120 }}>ЦЕНА</TableCell>
                                                    <TableCell sx={{ bgcolor: colors.light, fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', width: 140 }}>ОБНОВЛЕН</TableCell>
                                                    {hasStatusColumn && (
                                                        <TableCell sx={{ bgcolor: colors.light, fontWeight: 700, fontSize: '0.75rem', color: 'text.secondary', width: 250 }}>СТАТУС (ОШИБКИ)</TableCell>
                                                    )}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {document.items.map((item, index) => (
                                                    <AdminDocumentItemRow key={item.id || index} item={item} index={index} hasStatusColumn={hasStatusColumn} />
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </Box>
                        </Box>
                    </Box>
                </>
            ) : null}
        </Dialog>
    );
};

const AdminDocumentItemRow: React.FC<{ item: any; index: number; hasStatusColumn: boolean }> = ({ item, index, hasStatusColumn }) => {
    const hasMarks = item.markCodes && item.markCodes.length > 0;
    const itemStatus = item.ProcessStatus || item.processStatus;

    // Helper to format date
    const formatDate = (dateStr: string) => {
        if (!dateStr) return '—';
        try {
            return new Date(dateStr).toLocaleString('ru-RU', {
                day: '2-digit', month: '2-digit', year: '2-digit',
                hour: '2-digit', minute: '2-digit',
                timeZone: 'UTC'
            });
        } catch (e) {
            return dateStr;
        }
    };

    return (
        <TableRow hover sx={{ '&:last-child td': { border: 0 } }}>
            <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary', fontFamily: 'monospace' }}>{index + 1}</TableCell>
            <TableCell sx={{ fontSize: '0.85rem' }}>
                <Box>
                    <Typography variant="body2" sx={{ color: colors.dark }}>{item.materialName}</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box component="span">ШК:</Box> {item.barcode}
                            <Box component="span"> • </Box>
                            <Box component="span">Код:</Box> {item.materialCode}
                        </Typography>
                    </Box>
                </Box>
            </TableCell>
            <TableCell>
                {hasMarks ? (
                    <Tooltip title={item.markCodes.map((m: any) => m.mark).join('\n')} arrow placement="right">
                        <Chip
                            icon={<QrCode2 style={{ fontSize: 12 }} />}
                            label={`${item.markCodes.length} шт`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 22, fontSize: '0.75rem', borderColor: colors.primary, color: colors.primary, bgcolor: alpha(colors.primary, 0.05) }}
                        />
                    </Tooltip>
                ) : (
                    <Typography variant="caption" color="text.disabled">—</Typography>
                )}
            </TableCell>
            <TableCell sx={{ fontSize: '0.85rem' }}>
                {(() => {
                    const qty = parseFloat(item.quantity) || 0;
                    const qtyPart = parseFloat(item.quantityPcs || '0') || 0;
                    const parts: string[] = [];
                    if (qty > 0) parts.push(`${item.quantity} уп`);
                    if (qtyPart > 0) parts.push(`${item.quantityPcs} шт`);
                    return parts.length > 0 ? parts.join(' + ') : '—';
                })()}
            </TableCell>
            <TableCell sx={{ fontSize: '0.8rem', color: item.quantity1C ? 'text.primary' : 'text.disabled', fontFamily: item.quantity1C ? 'monospace' : 'inherit' }}>
                {item.quantity1C || '—'}
            </TableCell>
            <TableCell sx={{ fontSize: '0.8rem' }}>
                {item.stockPrice > 0 ? (
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark, lineHeight: 1.2 }}>
                            {item.lineTotal > 0
                                ? item.lineTotal.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₸'
                                : '—'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {item.stockPrice.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₸/уп
                        </Typography>
                    </Box>
                ) : (
                    <Typography variant="caption" color="text.disabled">—</Typography>
                )}
            </TableCell>
            <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                {formatDate(item.updateDate)}
            </TableCell>
            {hasStatusColumn && (
                <TableCell>
                    {itemStatus && itemStatus !== 'Проведен' && (
                        <Typography variant="caption" sx={{
                            color: colors.error,
                            fontWeight: 600,
                            display: 'block',
                            lineHeight: 1.2,
                            bgcolor: alpha(colors.error, 0.05),
                            p: 1,
                            borderRadius: 1,
                            border: `1px solid ${alpha(colors.error, 0.2)}`
                        }}>
                            {itemStatus}
                        </Typography>
                    )}
                </TableCell>
            )}
        </TableRow>
    );
};

export default AdminDocumentViewDialog;
